import { getSearchTabIdentity, normalizeSearch } from '@/utils/searchTabIdentity';
import { buildSearchTabTitle } from '@/utils/searchTabTitle';

export const RECENT_SEARCHES_MAX = 3;

export type RecentSearchEntry = {
  identity: string;
  title: string;
  search: string;
  updatedAt: number;
};

export function buildRecentSearchEntry(
  search: string,
  title?: string,
  now = Date.now(),
): RecentSearchEntry | null {
  const normalized = normalizeSearch(search);
  if (!normalized) return null;

  const params = new URLSearchParams(normalized.slice(1));
  const view = params.get('view') ?? '';
  const idents = params.get('idents') ?? '';
  const source = params.get('source') ?? 'ALL';
  const tab = params.get('tab') ?? 'GDA';

  if (!view || !idents || idents === 'ALL') return null;

  const cleanSearch = `?view=${view}&idents=${idents}&source=${source}&tab=${tab}`;
  const identity = getSearchTabIdentity(cleanSearch);
  const resolvedTitle = (title?.trim() || buildSearchTabTitle(null, cleanSearch)).trim();

  return {
    identity,
    title: resolvedTitle || idents.split('-').slice(0, 3).join(', '),
    search: cleanSearch,
    updatedAt: now,
  };
}

export function upsertRecentSearch(
  items: RecentSearchEntry[],
  entry: RecentSearchEntry,
  max = RECENT_SEARCHES_MAX,
): RecentSearchEntry[] {
  const withoutDuplicate = items.filter(item => item.identity !== entry.identity);
  return [entry, ...withoutDuplicate].slice(0, max);
}

export function entriesFromSearchTabs(
  tabs: Array<{ title: string; search: string; updatedAt: number }>,
  max = RECENT_SEARCHES_MAX,
): RecentSearchEntry[] {
  const sorted = [...tabs].sort((a, b) => b.updatedAt - a.updatedAt);
  const items: RecentSearchEntry[] = [];
  const seen = new Set<string>();

  for (const tab of sorted) {
    const entry = buildRecentSearchEntry(tab.search, tab.title, tab.updatedAt);
    if (!entry || seen.has(entry.identity)) continue;
    seen.add(entry.identity);
    items.push(entry);
    if (items.length >= max) break;
  }

  return items;
}
